/* eslint-disable react-hooks/exhaustive-deps */

import { useState, useEffect } from "react";
import { useAuth } from "context/AuthContext";
import {
  Header,
  Top3Ranking,
  Top5Ranking,
  ListRanking,
  PrivateRanking,
  SearchParticipant,
  AwardDisplay,
} from "components";
import { BannerPodiumPreview } from "components/Ranking/BannerPodiumPreview";
import { BannerPodiumTreasure } from "components/Ranking/BannerPodiumTreasure";
import { BannerPodiumMakeup } from "components/Ranking/BannerPodiumMakeup";
import { BannerPodiumTomb } from "components/Ranking/BannerPodiumTomb";
import { RankingPlayer, Ranking, LeaderboardResponse } from "types/ranking";
import { userToRankingPlayer } from "utils/ranking-helpers";
import {
  getLeaderboardsByPlayer,
  getLeaderboardLeaders,
} from "services/ranking.service";

import "./styles.scss";
import { Helmet } from "react-helmet-async";

// Dados de exemplo para demonstração
const mockRankingData: RankingPlayer[] = [
  {
    id: "1",
    name: "Leandro",
    fotoDePerfil: "Spidey", // Nome do avatar selecionado no onboarding
    position: 1,
    points: 2500,
    maxPoints: 3000,
    level: 12,
    teamName: "Equipe A",
    badges: 5,
    progress: 83,
  },
  {
    id: "2",
    name: "Ricardo",
    fotoDePerfil: "Steve",
    position: 2,
    points: 2300,
    maxPoints: 3000,
    level: 11,
    teamName: "Equipe B",
    badges: 4,
    progress: 77,
  },
  {
    id: "3",
    name: "Leandro",
    fotoDePerfil: "Tony",
    position: 3,
    points: 2100,
    maxPoints: 3000,
    level: 10,
    teamName: "Equipe A",
    badges: 3,
    progress: 70,
  },
  {
    id: "4",
    name: "Carlos",
    fotoDePerfil: "Wolvy",
    position: 4,
    points: 1950,
    maxPoints: 3000,
    level: 10,
    teamName: "Equipe C",
    badges: 3,
    progress: 65,
  },
  {
    id: "5",
    name: "Jorge",
    fotoDePerfil: "Superguy",
    position: 5,
    points: 1800,
    maxPoints: 3000,
    level: 9,
    teamName: "Equipe B",
    badges: 2,
    progress: 60,
  },
  {
    id: "6",
    name: "Luli",
    fotoDePerfil: "Wanda",
    position: 6,
    points: 1650,
    maxPoints: 3000,
    level: 9,
    teamName: "Equipe A",
    badges: 2,
    progress: 55,
  },
  {
    id: "7",
    name: "Gabriela",
    fotoDePerfil: "Wanda",
    position: 7,
    points: 1500,
    maxPoints: 3000,
    level: 8,
    teamName: "Equipe C",
    badges: 2,
    progress: 50,
  },
  {
    id: "8",
    name: "Fábio",
    fotoDePerfil: "Steve",
    position: 8,
    points: 1350,
    maxPoints: 3000,
    level: 8,
    teamName: "Equipe B",
    badges: 1,
    progress: 45,
  },
];

export function Home() {
  const { user, loadingUserData } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [playerLeaderboards, setPlayerLeaderboards] = useState<
    LeaderboardResponse[]
  >([]);
  const [leaderboardData, setLeaderboardData] = useState<{
    [key: string]: RankingPlayer[];
  }>({});
  const [loading, setLoading] = useState(true);

  // Converte LeaderboardLeader para RankingPlayer
  const leaderToPlayer = (leader: any, maxPoints: number): RankingPlayer => ({
    id: leader.player || leader._id,
    name: leader.name || "Jogador",
    avatar: leader.image,
    fotoDePerfil: leader.image,
    position: leader.position,
    points: leader.total || 0,
    maxPoints: maxPoints,
    level: Math.floor((leader.total || 0) / 200) + 1,
    progress: Math.floor(((leader.total || 0) % 200) / 2),
  });

  // Extrai configuração do ranking do campo extra do leaderboard
  const getRankingConfig = (
    leaderboard: LeaderboardResponse
  ): Ranking | null => {
    try {
      if (leaderboard.extra && typeof leaderboard.extra === "object") {
        return leaderboard.extra as Ranking;
      }
      return null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    let isMounted = true;
    let isLoading = false;

    async function loadPlayerLeaderboards() {
      if (isLoading) return;

      if (!user?._id) {
        setLoading(false);
        return;
      }

      try {
        isLoading = true;
        setLoading(true);

        const leaderboards = await getLeaderboardsByPlayer(
          user._id,
          user.email
        );

        if (!isMounted) return;

        if (leaderboards.length === 0) {
          setPlayerLeaderboards([]);
          setLeaderboardData({});
          return;
        }

        setPlayerLeaderboards(leaderboards);

        const leadersPromises = leaderboards
          .filter((lb) => lb._id)
          .map(async (leaderboard) => {
            const id = leaderboard._id as string;
            try {
              const leaders = await getLeaderboardLeaders(id);

              const maxPoints =
                leaders.length > 0
                  ? Math.max(...leaders.map((l) => l.total || 0))
                  : 3000;

              return {
                id,
                players: leaders.map((leader) =>
                  leaderToPlayer(leader, maxPoints)
                ),
              };
            } catch (error) {
              return { id, players: [] };
            }
          });

        const leadersResults = await Promise.all(leadersPromises);

        if (!isMounted) return;

        const leaderboardsData: { [key: string]: RankingPlayer[] } = {};
        leadersResults.forEach((result) => {
          leaderboardsData[result.id] = result.players;
        });

        setLeaderboardData(leaderboardsData);
      } catch (error) {
        if (isMounted) {
          setPlayerLeaderboards([]);
          setLeaderboardData({});
        }
      } finally {
        isLoading = false;
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadPlayerLeaderboards();

    return () => {
      isMounted = false;
    };
  }, [user?._id, user?.email]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  // Filtra jogadores baseado na busca para um ranking específico
  const getFilteredPlayers = (players: RankingPlayer[]) => {
    if (!searchQuery.trim()) return players;

    return players.filter(
      (player) =>
        player.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        player.teamName?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  return (
    <>
      <Helmet>
        <title>Dashboard - Rankifier</title>
      </Helmet>
      <div className="home-wrapper">
        <Header />
        <div className="home-content">
          <div className="z-10 relative flex flex-col items-center w-full">
            <div className="w-full max-w-7xl px-4" id="homeLogin">
              {loadingUserData ? (
                <div className="loading-rankings">
                  <div className="spinner"></div>
                  <p>Carregando...</p>
                </div>
              ) : user ? (
                <div className="rankings-container">
                  {/* Busca de Participantes */}
                  <SearchParticipant onSearch={handleSearch} />

                  {loading ? (
                    <div className="loading-rankings">
                      <div className="spinner"></div>
                      <p>Carregando rankings...</p>
                    </div>
                  ) : playerLeaderboards.length === 0 ? (
                    <div className="no-rankings">
                      <p>Você ainda não está participando de nenhum ranking.</p>
                    </div>
                  ) : (
                    /* Rankings - Um abaixo do outro */
                    <div className="rankings-grid">
                      {playerLeaderboards.map((leaderboard) => {
                        if (!leaderboard._id) return null;

                        // Extrai configuração do ranking do extra
                        const rankingConfig = getRankingConfig(leaderboard);
                        if (!rankingConfig) {
                          return null;
                        }

                        // Garante que visuals seja um array
                        const visuals = Array.isArray(rankingConfig.visuals)
                          ? rankingConfig.visuals
                          : [];

                        if (visuals.length === 0) {
                          return null;
                        }

                        const players = leaderboardData[leaderboard._id] || [];
                        const filteredPlayers = getFilteredPlayers(players);

                        return (
                          <div
                            key={leaderboard._id}
                            className="ranking-section"
                          >
                            <div className="flex gap-4 xl:flex-col">
                              <h2 className="ranking-section-title">
                                {leaderboard.title}
                              </h2>
                              <p className="ranking-section-meta flex items-center">
                                {rankingConfig.period === "semanal" &&
                                  "Ranking Semanal"}
                                {rankingConfig.period === "mensal" &&
                                  "Ranking Mensal"}
                                {rankingConfig.period === "anual" &&
                                  "Ranking Anual"}
                                {" • "}
                                {rankingConfig.metric === "points" && "Pontos"}
                                {rankingConfig.metric === "tasks" && "Tarefas"}
                                {rankingConfig.metric === "custom" &&
                                  "Personalizado"}
                              </p>
                            </div>

                            {/* Exibir prêmio se configurado e showAward for true */}
                            {rankingConfig.award?.showAward && 
                             rankingConfig.award?.awardDetails && (
                              <AwardDisplay 
                                award={rankingConfig.award.awardDetails}
                                variant="banner"
                              />
                            )}

                            {/* Renderiza componente baseado nos visuals configurados */}
                            {visuals.includes("podio") && (
                              <Top3Ranking
                                players={filteredPlayers.slice(0, 3)}
                                title={`Top 3 - ${leaderboard.title}`}
                              />
                            )}

                            {visuals.includes("grid") && (
                              <Top5Ranking
                                players={filteredPlayers.slice(0, 5)}
                                title={`Top 5 - ${leaderboard.title}`}
                              />
                            )}

                            {visuals.includes("lista") && (
                              <ListRanking
                                players={filteredPlayers}
                                currentUserId={user._id}
                                title={`Lista Completa - ${leaderboard.title}`}
                              />
                            )}

                            {visuals.includes("privado") && (
                              <PrivateRanking
                                players={filteredPlayers}
                                currentUserId={user._id}
                                title={`Meu Ranking - ${leaderboard.title}`}
                              />
                            )}

                            {visuals.includes("medieval") && (
                              <div className="rankings-side-by-side">
                                {/* Pódio Medieval (70%) */}
                                <div className="ranking-left">
                                  <BannerPodiumPreview
                                    players={filteredPlayers.slice(0, 3)}
                                    title="Top 3"
                                    isLoading={loading}
                                  />
                                </div>

                                {/* Lista Completa (30%) */}
                                <div className="ranking-right">
                                  <ListRanking
                                    players={filteredPlayers}
                                    currentUserId={user._id || user.email || ""}
                                    title="Ranking Completo"
                                  />
                                </div>
                              </div>
                            )}

                            {visuals.includes("tesouro") && (
                              <div className="rankings-side-by-side">
                                {/* Pódio do Tesouro (70%) */}
                                <div className="ranking-left">
                                  <BannerPodiumTreasure
                                    players={filteredPlayers.slice(0, 3)}
                                    title="Top 3"
                                    isLoading={loading}
                                  />
                                </div>

                                {/* Lista Completa (30%) */}
                                <div className="ranking-right">
                                  <ListRanking
                                    players={filteredPlayers}
                                    currentUserId={user._id || user.email || ""}
                                    title="Ranking Completo"
                                  />
                                </div>
                              </div>
                            )}

                            {visuals.includes("maquiagem") && (
                              <div className="rankings-side-by-side">
                                {/* Pódio de Maquiagem (70%) */}
                                <div className="ranking-left">
                                  <BannerPodiumMakeup
                                    players={filteredPlayers.slice(0, 3)}
                                    title="Top 3"
                                    isLoading={loading}
                                  />
                                </div>

                                {/* Lista Completa (30%) */}
                                <div className="ranking-right">
                                  <ListRanking
                                    players={filteredPlayers}
                                    currentUserId={user._id || user.email || ""}
                                    title="Ranking Completo"
                                  />
                                </div>
                              </div>
                            )}

                            {visuals.includes("tumba") && (
                              <div className="rankings-side-by-side">
                                {/* Pódio da Tumba (70%) */}
                                <div className="ranking-left">
                                  <BannerPodiumTomb
                                    players={filteredPlayers.slice(0, 3)}
                                    title="Top 3"
                                    isLoading={loading}
                                  />
                                </div>

                                {/* Lista Completa (30%) */}
                                <div className="ranking-right">
                                  <ListRanking
                                    players={filteredPlayers}
                                    currentUserId={user._id || user.email || ""}
                                    title="Ranking Completo"
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
